import * as Yup from 'yup';
import {
  SurveyQuestion,
  SurveyQuestionAnswerInput,
  SurveyQuestionType,
  DecimalProps,
} from '@fdha/graphql-api-admin';
import { requiredMessage } from '@fdha/web-ui-library';

const validationSchemaBySurveyQuestionType: Partial<
  Record<SurveyQuestionType, Yup.AnySchema | undefined>
> = {
  [SurveyQuestionType.Number]: Yup.string().test(
    'is-valid-number',
    'Please insert a valid number',
    (value = '') => {
      if (!value) return true;
      const numberRegex = /^\d+$/;
      return numberRegex.test(value);
    }
  ),
  [SurveyQuestionType.Time]: Yup.string()
    .matches(/^\d{2}:\d{2} (am|pm)$/i, 'Time must be in the format HH:MM AM/PM')
    .test('is-valid-time', 'Please insert a valid time', (value) => {
      if (!value) return true;
      const [time, period] = value.toLowerCase().split(' ');
      const [hour, minutes] = time.split(':');

      // Validate hour and minutes
      const hourNum = parseInt(hour, 10);
      const minutesNum = parseInt(minutes, 10);

      const isValidHour = hourNum >= 1 && hourNum <= 12;
      const isValidMinutes = minutesNum >= 0 && minutesNum <= 59;
      const isValidPeriod = ['am', 'pm'].includes(period);

      return isValidHour && isValidMinutes && isValidPeriod;
    }),
  [SurveyQuestionType.Decimal]: Yup.string()
    .trim()
    .test('is-valid-decimal', 'Please insert a valid decimal', (value = '') => {
      if (!value) return true;
      const decimalRegex = /^-?\d+(\.\d+)?$/;
      return decimalRegex.test(value);
    }),
  [SurveyQuestionType.Date]: Yup.string().test(
    'is-valid-date',
    'Please insert a valid date',
    (value = '') => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
  ),
};

export const validateAnswers = (
  answers: SurveyQuestionAnswerInput[],
  questions?: SurveyQuestion[]
): {
  questionId: string;
  message: string;
}[] => {
  const answerErrors: {
    questionId: string;
    message: string;
  }[] = [];

  questions?.forEach((question) => {
    const answer = answers.find((a) => a.questionId === question.id);
    const isRequired =
      question?.required && (!answer || answer?.answer?.length === 0);
    if (isRequired) {
      answerErrors.push({
        questionId: question.id,
        message: requiredMessage,
      });
      return;
    }
    // If the question is not required and there is no answer, skip validation
    if (!answer || answer?.answer?.length === 0) {
      return;
    }
    // If there is an answer, validate the answer
    if (question) {
      let validationSchema =
        validationSchemaBySurveyQuestionType[question.type];

      // Special case for Decimal questions with min/max constraints
      if (
        question.type === SurveyQuestionType.Decimal &&
        question.customPropsOfType &&
        typeof question.customPropsOfType === 'object'
      ) {
        try {
          const decimalProps = question.customPropsOfType as DecimalProps;
          const { min, max, unit } = decimalProps;

          if (typeof min === 'number' && typeof max === 'number') {
            const unitDisplay = unit ? ` ${unit}` : '';
            const errorMessage = `Please insert a value between ${min} - ${max}${unitDisplay}`;

            validationSchema = Yup.string()
              .test(
                'is-decimal',
                'Please insert a valid decimal',
                (value = '') => {
                  if (!value) return true;
                  const decimalRegex = /^-?\d+(\.\d+)?$/;
                  return decimalRegex.test(value);
                }
              )
              .test('is-in-range', errorMessage, (value = '') => {
                if (!value) return true;
                const numValue = parseFloat(value);
                return !isNaN(numValue) && numValue >= min && numValue <= max;
              });
          }
        } catch (error) {
          // Fall back to basic validation
          console.error('Error validating decimal value:', error);
        }
      }

      try {
        validationSchema?.validateSync(answer?.answer[0]);
      } catch (error) {
        const validationError = error as Yup.ValidationError;
        answerErrors.push({
          questionId: question.id,
          message: validationError.message,
        });
      }
    }
  });

  return answerErrors;
};
